import {
	Box,
	ButtonPrimary,
	Callout,
	EmailField,
	IconInformationUserRegular,
	Select,
	Stack,
	Text3,
	TextField,
	skinVars,
	useScreenSize,
} from "@telefonica/mistica";
import React from "react";
import { strings } from "../../localization";
import { observer } from "mobx-react-lite";
import { isEmailValid, isPhoneNumberValid } from "../../utils/validators";
import { formatCellphoneNumber, onlyNumber } from "../../utils/formatters";
import { useAnalytics } from "../../hooks/useAnalytics";

export interface PixData {
	documentNumber?: string;
	pixKeyType: string;
	pixKey: string;
}

interface IProps {
	title: string;
	description: string;
	documentNumber: string;
	onContinue(data: PixData): void;
}

export const WithdrawPix: React.FC<IProps> = observer((props) => {
	const { title, description, documentNumber, onContinue } = props;
	const [pixKeyType, setPixKeyType] = React.useState("");
	const [pixKey, setPixKey] = React.useState("");
	const { cpfTitular, keyType, cpf, email, cellPhone, random, buttonPrimary, errors } = strings.components.withdrawPix;
	const { isDesktopOrBigger } = useScreenSize();
	const { addAnalyticEvent } = useAnalytics();

	const disable = !pixKeyType || (pixKey.length === 0 && pixKeyType !== "cpf");
	const isValidInput =
		pixKeyType !== "random" && pixKeyType !== "cpf" && !(isEmailValid(pixKey) || isPhoneNumberValid(pixKey));

	const keyOptions = [
		{ value: "cpf", text: cpf },
		{ value: "email", text: email },
		{ value: "cellPhone", text: cellPhone },
		{ value: "random", text: random },
	];

	const handleContinue = (): void => {
		if (pixKeyType === "cpf") {
			onContinue({ pixKeyType: "cpf", pixKey: documentNumber });
			return;
		}
		onContinue({ pixKeyType, pixKey, documentNumber });
	};

	React.useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (isValidInput) {
				addAnalyticEvent({ event: "non_interaction", action: "view-error", step: "fgts:recebimento:pix", description: "chave-invalida" }, { applySanitize: true });
				return;
			}

			if (pixKeyType === "email") {
				addAnalyticEvent({ event: "non_interaction", action: "view-success", step: "fgts:recebimento:pix", description: "chave-invalida" }, { applySanitize: true });
			}
		}, 1600);

		return () => clearTimeout(timeoutId);
	}, [isValidInput]);

	return (
		<Box>
			<Stack space={16}>
				<Text3 medium color={skinVars.colors.textPrimary}>
					{title}
				</Text3>
				<TextField readOnly fullWidth label={cpfTitular} name="name" value={documentNumber} disabled data-cs-mask />
				<Select
					label={keyType}
					fullWidth
					onChangeValue={(newValue) => {
						setPixKeyType(newValue);
						setPixKey("");
					}}
					value={pixKeyType}
					name="normal"
					options={keyOptions}
				/>
				{pixKeyType === "email" && (
					<EmailField
						fullWidth
						label={email}
						placeholder={email}
						name="e-mail"
						value={pixKey}
						onChangeValue={setPixKey}
						error={!isEmailValid(pixKey) && pixKey !== ""}
						helperText={!isEmailValid(pixKey) && pixKey !== "" ? errors.invalidPixKey : ""}
						data-cs-mask
					/>
				)}
				{pixKeyType === "cellPhone" && (
					<TextField
						fullWidth
						label={cellPhone}
						name="cellPhone"
						maxLength={15}
						value={pixKey}
						onChangeValue={(newValue) => {
							const formattedNumber = formatCellphoneNumber(onlyNumber(newValue));
							setPixKey(formattedNumber);
						}}
						error={!isPhoneNumberValid(pixKey) && pixKey !== ""}
						helperText={pixKey.length !== 15 && pixKey !== "" ? errors.invalidPixKey : ""}
						data-cs-mask
					/>
				)}
				{pixKeyType === "random" && (
					<TextField fullWidth label={random} name="random" value={pixKey} onChangeValue={setPixKey} />
				)}
				<Callout icon={<IconInformationUserRegular color={skinVars.colors.brand} />} description={description} />
			</Stack>
			<Box paddingTop={32}>
				<ButtonPrimary
					onPress={handleContinue}
					disabled={disable || isValidInput}
					style={{ width: isDesktopOrBigger ? "auto" : "100%" }}
				>
					{buttonPrimary}
				</ButtonPrimary>
			</Box>
		</Box>
	);
});
